import React, { useState } from 'react';
import { Box, Text, Button, Flex, Input } from '@chakra-ui/react';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { getAccountStatement } from '../../queries';
import {
  StatementHeader,
  BalanceCards,
  SummaryCards,
  FundingTable,
  TransactionsTable,
} from '../../components/merchant/accountStatement';
import { SpinningText } from '../../components/common/loaders';
import { exportAccountStatementPDF } from '../../utils';
import instance from '../../queries/axios.config';

const AccountStatement = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);

  const { data, isLoading, isError, refetch } = useQuery(
    ['accountStatement', appliedStartDate, appliedEndDate, currentPage, limit],
    () =>
      getAccountStatement(
        'accountStatement',
        appliedStartDate,
        appliedEndDate,
        currentPage,
        limit
      ),
    {
      enabled: hasGenerated,
      keepPreviousData: true,
    }
  );

  const handleGenerateStatement = () => {
    setAppliedStartDate(startDate || null);
    setAppliedEndDate(endDate || null);
    setCurrentPage(1);
    setHasGenerated(true);
    refetch();
  };

  const handleFilter = () => {
    if (startDate && endDate) {
      setAppliedStartDate(startDate);
      setAppliedEndDate(endDate);
      setCurrentPage(1);
      refetch();
    }
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setCurrentPage(1);
    refetch();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleExportPDF = () => {
    if (data) {
      exportAccountStatementPDF(data);
    }
  };

  const handleExportCSV = async () => {
    try {
      let url = '/merchant/account-statement/export';
      const params = new URLSearchParams();

      if (appliedStartDate) {
        params.append('startDate', appliedStartDate);
      }
      if (appliedEndDate) {
        params.append('endDate', appliedEndDate);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await instance.get(url, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      const fileName = appliedStartDate && appliedEndDate
        ? `account_statement_${appliedStartDate}_to_${appliedEndDate}.csv`
        : `account_statement_${dayjs().format('YYYY-MM-DD')}.csv`;

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV. Please try again.');
      console.error('CSV export error:', error);
    }
  };

  return (
    <Box maxW="100%" w="100%" overflowX="hidden">
      <Box mb="2rem">
        <Text
          fontFamily="bold"
          fontSize="32px"
          color="#212d43"
          lineHeight="38px"
          mb="1rem"
        >
          Account Statement
        </Text>
        {data && (
          <Flex gap="1rem">
            <Button
              bg="white"
              color="brand.blue"
              border="1px solid"
              borderColor="brand.blue"
              fontSize="14px"
              fontFamily="medium"
              px="2rem"
              py="1.5rem"
              borderRadius="8px"
              _hover={{ bg: '#EBF4FF' }}
              _active={{ bg: '#D6E9FF' }}
              onClick={handleExportCSV}
            >
              Export CSV
            </Button>
            <Button
              bg="brand.blue"
              color="white"
              fontSize="14px"
              fontFamily="medium"
              px="2rem"
              py="1.5rem"
              borderRadius="8px"
              _hover={{ bg: '#1558d6' }}
              _active={{ bg: '#0d47a1' }}
              onClick={handleExportPDF}
            >
              Export PDF
            </Button>
          </Flex>
        )}
      </Box>

      <Box bg="white" p="2rem" borderRadius="8px" mb="2rem" boxShadow="md">
        <Text
          fontSize="18px"
          fontFamily="bold"
          color="brand.header"
          mb="1.5rem"
        >
          Filter by Date Range
        </Text>
        <Flex gap="1rem" alignItems="flex-end" flexWrap="wrap">
          <Box flex="1" minW="200px">
            <Text
              fontSize="14px"
              fontFamily="medium"
              color="#555555"
              mb="0.5rem"
            >
              Start Date
            </Text>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate || dayjs().format('YYYY-MM-DD')}
              bg="white"
              h="45px"
              borderColor="#D1D5DB"
              borderWidth="1px"
              _hover={{ borderColor: 'brand.blue' }}
              _focus={{
                borderColor: 'brand.blue',
                boxShadow: '0 0 0 1px #1a73e8',
              }}
            />
          </Box>
          <Box flex="1" minW="200px">
            <Text
              fontSize="14px"
              fontFamily="medium"
              color="#555555"
              mb="0.5rem"
            >
              End Date
            </Text>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              max={dayjs().format('YYYY-MM-DD')}
              bg="white"
              h="45px"
              borderColor="#D1D5DB"
              borderWidth="1px"
              _hover={{ borderColor: 'brand.blue' }}
              _focus={{
                borderColor: 'brand.blue',
                boxShadow: '0 0 0 1px #1a73e8',
              }}
            />
          </Box>
          {hasGenerated ? (
            <>
              <Button
                bg="brand.blue"
                color="white"
                fontSize="14px"
                fontFamily="medium"
                h="45px"
                px="2rem"
                borderRadius="8px"
                _hover={{ bg: '#1558d6' }}
                _active={{ bg: '#0d47a1' }}
                onClick={handleFilter}
                isDisabled={!startDate || !endDate}
              >
                Apply Filter
              </Button>
              {(appliedStartDate || appliedEndDate) && (
                <Button
                  bg="white"
                  color="brand.blue"
                  border="1px solid"
                  borderColor="brand.blue"
                  fontSize="14px"
                  fontFamily="medium"
                  h="45px"
                  px="2rem"
                  borderRadius="8px"
                  _hover={{ bg: '#EBF4FF' }}
                  _active={{ bg: '#D6E9FF' }}
                  onClick={handleClearFilter}
                >
                  Clear Filter
                </Button>
              )}
            </>
          ) : (
            <Button
              bg="brand.blue"
              color="white"
              fontSize="14px"
              fontFamily="medium"
              h="45px"
              px="2.5rem"
              borderRadius="8px"
              _hover={{ bg: '#1558d6' }}
              _active={{ bg: '#0d47a1' }}
              onClick={handleGenerateStatement}
              isLoading={isLoading}
            >
              Generate Statement
            </Button>
          )}
        </Flex>
      </Box>

      {isLoading && <SpinningText />}

      {isError && !isLoading && (
        <Box
          bg="white"
          p="4rem"
          borderRadius="8px"
          textAlign="center"
          boxShadow="md"
        >
          <Text fontSize="18px" color="#E24D4D" fontFamily="medium">
            Failed to load account statement. Please try again.
          </Text>
        </Box>
      )}

      {!isLoading && !isError && data && (
        <>
          <StatementHeader merchant={data.merchant} period={data.period} />
          <BalanceCards balance={data.balance} />
          <SummaryCards summary={data.summary} />
          <FundingTable funding={data.funding} />
          <TransactionsTable transactions={data.transactions} />

          {data.pagination && data.pagination.total_pages > 1 && (
            <Flex
              justifyContent="space-between"
              alignItems="center"
              mt="2rem"
              p="1.5rem"
              bg="white"
              borderRadius="8px"
              boxShadow="md"
            >
              <Text fontSize="14px" color="#666">
                Page {data.pagination.current_page} of {data.pagination.total_pages}
                {' '}
                ({data.pagination.total_records} total records)
              </Text>
              <Flex gap="0.5rem">
                <Button
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  isDisabled={currentPage === 1 || isLoading}
                  bg="white"
                  border="1px solid"
                  borderColor="brand.blue"
                  color="brand.blue"
                  _hover={{ bg: '#EBF4FF' }}
                  _disabled={{
                    opacity: 0.4,
                    cursor: 'not-allowed',
                  }}
                >
                  Previous
                </Button>
                <Flex gap="0.25rem">
                  {Array.from(
                    { length: data.pagination.total_pages },
                    (_, i) => i + 1
                  )
                    .filter(
                      (page) =>
                        page === 1 ||
                        page === data.pagination.total_pages ||
                        Math.abs(page - currentPage) <= 2
                    )
                    .map((page, index, array) => {
                      if (index > 0 && page - array[index - 1] > 1) {
                        return (
                          <React.Fragment key={`ellipsis-${page}`}>
                            <Text px="0.5rem" color="#666">
                              ...
                            </Text>
                            <Button
                              key={page}
                              size="sm"
                              onClick={() => handlePageChange(page)}
                              bg={
                                currentPage === page ? 'brand.blue' : 'white'
                              }
                              color={
                                currentPage === page ? 'white' : 'brand.blue'
                              }
                              border="1px solid"
                              borderColor="brand.blue"
                              _hover={{
                                bg:
                                  currentPage === page
                                    ? '#1558d6'
                                    : '#EBF4FF',
                              }}
                              minW="40px"
                            >
                              {page}
                            </Button>
                          </React.Fragment>
                        );
                      }
                      return (
                        <Button
                          key={page}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          bg={currentPage === page ? 'brand.blue' : 'white'}
                          color={
                            currentPage === page ? 'white' : 'brand.blue'
                          }
                          border="1px solid"
                          borderColor="brand.blue"
                          _hover={{
                            bg:
                              currentPage === page ? '#1558d6' : '#EBF4FF',
                          }}
                          minW="40px"
                        >
                          {page}
                        </Button>
                      );
                    })}
                </Flex>
                <Button
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  isDisabled={
                    currentPage === data.pagination.total_pages || isLoading
                  }
                  bg="white"
                  border="1px solid"
                  borderColor="brand.blue"
                  color="brand.blue"
                  _hover={{ bg: '#EBF4FF' }}
                  _disabled={{
                    opacity: 0.4,
                    cursor: 'not-allowed',
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Flex>
          )}
        </>
      )}

      {!isLoading && !isError && !data && hasGenerated && (
        <Box
          bg="white"
          p="4rem"
          borderRadius="8px"
          textAlign="center"
          boxShadow="md"
        >
          <Text fontSize="18px" color="#808080">
            No account statement data available for the selected period.
          </Text>
        </Box>
      )}

      {!hasGenerated && !isLoading && (
        <Box
          bg="white"
          p="6rem"
          borderRadius="8px"
          textAlign="center"
          boxShadow="md"
        >
          <Text
            fontSize="20px"
            fontFamily="bold"
            color="brand.header"
            mb="1rem"
          >
            Generate Your Account Statement
          </Text>
          <Text fontSize="16px" color="#808080" mb="2rem">
            Click the &quot;Generate Statement&quot; button above to view your
            account statement.
            <br />
            You can optionally select a date range to filter transactions.
          </Text>
        </Box>
      )}
    </Box>
  );
};

export { AccountStatement };
